import { ButtonBase, styled } from '@mui/material';
import classNames from 'classnames';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import css from './ActionButton.module.scss';

export interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  shineBehind?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'primary-cta' | 'secondary-cta';
  fullWidth?: boolean;
  padding?: number;
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

const StyledButtonBase = styled(ButtonBase)(({ theme }) => ({
  userSelect: 'none',
  fontFamily: 'Inter',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0%',
  height: '40px',
  width: 'fit-content',
  padding: '0 16px',
  borderRadius: '32px',
  backgroundColor: 'white',
  color: 'black',
  border: 'none',
  cursor: 'pointer',
  position: 'relative',
  zIndex: 1,
  transition: 'background-color 0.2s, outline 0.2s',
  whiteSpace: 'nowrap',
  outline: 'none',

  '&:focus-visible': {
    outline: '2px solid #d0a9ef',
    outlineOffset: '2px',
  },

  '&:hover': {
    backgroundColor: '#f8f9fa',
  },

  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(193, 193, 194, 0.4)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },

  '&.secondary': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: 'white',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },

    '&:focus-visible': {
      outline: '2px solid #d0a9ef',
      outlineOffset: '2px',
    },
  },

  '&.danger': {
    backgroundColor: '#d64550',
    color: 'white',

    '&:hover': {
      backgroundColor: 'darkred',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },

    '&:focus-visible': {
      outline: '2px solid #ff6b6b',
      outlineOffset: '2px',
    },
  },

  '&.primary-cta': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    color: 'black',

    '&:hover': {
      backgroundColor: 'rgba(248, 249, 250, 1)',
    },

    '&:focus-visible': {
      outline: '2px solid #d0a9ef',
      outlineOffset: '2px',
    },
  },

  '&.secondary-cta': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)!important',
    color: 'white',
  },
}));

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    { children, shineBehind, variant = 'primary', fullWidth = false, padding, ...buttonProps },
    ref,
  ) => {
    return (
      <div
        className={classNames(css.actionButtonContainer, {
          [css.fullWidth]: fullWidth,
          [css.primaryCta]: variant === 'primary-cta',
          [css.disabled]: buttonProps.disabled,
        })}
      >
        <StyledButtonBase
          ref={ref}
          {...buttonProps}
          className={classNames(
            buttonProps.className,
            variant === 'secondary' && 'secondary',
            variant === 'danger' && 'danger',
            variant === 'primary-cta' && 'primary-cta',
            variant === 'secondary-cta' && 'secondary-cta',
            { [css.fullWidthButton]: fullWidth },
          )}
          disableRipple={false}
          style={{
            ...buttonProps.style,
            ...(padding ? { paddingLeft: padding, paddingRight: padding } : undefined),
          }}
        >
          {children}
        </StyledButtonBase>
      </div>
    );
  },
);
